import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { SearchX, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { EmptyState } from "~/components/ui/empty-state";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  adminCoachDirectoryListQueryOptions,
  adminCoachDirectorySummaryQueryOptions,
  adminCoachReadinessQueryOptions,
  exportAdminCoachDirectoryFn,
} from "~/lib/functions/admin-coach-directory";
import {
  ACCOUNT_STATUSES,
  ACTIVITY_FILTERS,
  COACH_DIRECTORY_PAGE_SIZES,
  COACH_DIRECTORY_SORTS,
  COACH_SOURCES,
  COACH_TAGS,
  COACH_USE_FILTERS,
  type CoachDirectoryListParams,
  type CoachDirectoryRow,
  type CoachDirectorySort,
  LISTING_STATUSES,
  NOVA_STATES,
} from "~/lib/shared/admin-coach-directory";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { hasPermission } from "~/lib/shared/permissions";
import {
  nextSortSearch,
  SORT_DIRECTIONS,
  type SortDefaults,
  type SortDirection,
} from "~/lib/shared/sort";

import { CoachDetailSheet } from "./-components/coach-detail-sheet";
import {
  buildCoachDirectoryCsv,
  COACH_DIRECTORY_COLUMN_IDS,
  COACH_DIRECTORY_VIEW_IDS,
  type CoachDirectoryColumnId,
  DEFAULT_VIEW,
  asksForReadiness,
  FACET_KEYS,
  type FacetFilters,
  isReadinessColumn,
  VIEWS,
} from "./-components/coach-directory-model";
import { CoachDirectorySummaryStrip } from "./-components/coach-directory-summary";
import {
  CoachDirectoryPagination,
  CoachDirectoryTable,
  SelectionBar,
} from "./-components/coach-directory-table";
import {
  ActiveFilterChips,
  CoachDirectoryToolbar,
  CoachDirectoryViewTabs,
} from "./-components/coach-directory-toolbar";
import { useDebouncedSearchParam } from "./-components/use-debounced-search-param";

// A stale or hand-edited link drops the params it gets wrong instead of
// failing the whole page.
const coachDirectorySearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  size: z
    .union([z.literal(25), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined),
  sort: z.enum(COACH_DIRECTORY_SORTS).optional().catch(undefined),
  dir: z.enum(SORT_DIRECTIONS).optional().catch(undefined),
  view: z.enum(COACH_DIRECTORY_VIEW_IDS).optional().catch(undefined),
  cols: z.array(z.enum(COACH_DIRECTORY_COLUMN_IDS)).optional().catch(undefined),
  account: z.enum(ACCOUNT_STATUSES).optional().catch(undefined),
  listing: z.enum(LISTING_STATUSES).optional().catch(undefined),
  nova: z.enum(NOVA_STATES).optional().catch(undefined),
  source: z.enum(COACH_SOURCES).optional().catch(undefined),
  activity: z.enum(ACTIVITY_FILTERS).optional().catch(undefined),
  tag: z.enum(COACH_TAGS).optional().catch(undefined),
  use: z.enum(COACH_USE_FILTERS).optional().catch(undefined),
});

type CoachDirectorySearch = z.infer<typeof coachDirectorySearchSchema>;

const DEFAULT_PAGE_SIZE = COACH_DIRECTORY_PAGE_SIZES[0];

/** Names read best A–Z; every other sort starts newest or largest. */
const SORT_DEFAULTS: SortDefaults<CoachDirectorySort> = {
  sort: "joined",
  direction: (sort): SortDirection => (sort === "name" ? "asc" : "desc"),
};

function facetsFrom(search: CoachDirectorySearch): FacetFilters {
  return {
    account: search.account,
    listing: search.listing,
    nova: search.nova,
    source: search.source,
    activity: search.activity,
    tag: search.tag,
    use: search.use,
  };
}

function listParamsFrom(
  search: CoachDirectorySearch,
): CoachDirectoryListParams {
  const sort = search.sort ?? SORT_DEFAULTS.sort;
  return {
    ...facetsFrom(search),
    search: search.q ?? "",
    sort,
    direction: search.dir ?? SORT_DEFAULTS.direction(sort),
    page: search.page ?? 1,
    pageSize: search.size ?? DEFAULT_PAGE_SIZE,
  };
}

/** Every facet key, so applying a set of facets also clears the others. */
function facetSearchPatch(facets: FacetFilters): Partial<CoachDirectorySearch> {
  return { ...facetsFrom(facets), page: undefined };
}

function downloadCsv(csv: string, name: string): void {
  // The byte-order mark makes Excel read the file as UTF-8.
  const url = URL.createObjectURL(
    new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const Route = createFileRoute("/admin/coaches/")({
  staticData: { title: "Coach Directory" },
  validateSearch: coachDirectorySearchSchema,
  loaderDeps: ({ search }) => listParamsFrom(search),
  beforeLoad: ({ context, search }) => {
    requirePermission(context.adminPermissions, "coaches:read");
    // A shared link to the Readiness view opens the plain directory for staff
    // who may not read the scores, rather than failing.
    if (
      asksForReadiness(search) &&
      !hasPermission(context.adminPermissions, "coach-assessments:read")
    ) {
      throw redirect({
        to: "/admin/coaches",
        search: {
          ...search,
          view: undefined,
          cols: undefined,
          sort: search.sort === "readiness" ? undefined : search.sort,
        },
      });
    }
  },
  loader: async ({ context, deps }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(
        adminCoachDirectoryListQueryOptions(deps),
      ),
      context.queryClient.ensureQueryData(
        adminCoachDirectorySummaryQueryOptions(deps.search),
      ),
    ]);
  },
  head: () =>
    createNoIndexMeta(
      "Admin - Coach Directory – Dream Coach Match",
      "Find, review, act on, and export coach accounts.",
    ),
  component: AdminCoachDirectoryPage,
});

function AdminCoachDirectoryPage(): React.JSX.Element {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { adminPermissions } = Route.useRouteContext();
  const canSeeReadiness = hasPermission(
    adminPermissions,
    "coach-assessments:read",
  );
  const canEditCoaches = hasPermission(adminPermissions, "coaches:write");
  const params = listParamsFrom(search);
  const facets = facetsFrom(search);
  const view = search.view ?? DEFAULT_VIEW;
  const columns = search.cols ?? VIEWS[view].columns;
  const isCustomColumns = search.cols !== undefined;
  // One clock per visit, so relative dates do not drift between renders.
  const [now] = useState(() => new Date());

  const updateSearch = (
    patch: Partial<CoachDirectorySearch>,
    options: { replace?: boolean } = {},
  ) =>
    navigate({
      search: (previous) => ({ ...previous, ...patch }),
      replace: options.replace ?? false,
    });

  const [searchInput, setSearchInput] = useDebouncedSearchParam(
    params.search,
    (query) =>
      navigate({
        search: (previous) => ({
          ...previous,
          q: query === "" ? undefined : query,
          page: undefined,
        }),
        replace: true,
      }),
  );

  const list = useQuery({
    ...adminCoachDirectoryListQueryOptions(params),
    placeholderData: keepPreviousData,
  });
  const summary = useQuery({
    ...adminCoachDirectorySummaryQueryOptions(params.search),
    placeholderData: keepPreviousData,
  });

  const rows = list.data?.rows ?? [];
  const readiness = useQuery({
    ...adminCoachReadinessQueryOptions(rows.map((coach) => coach.id)),
    enabled:
      canSeeReadiness && rows.length > 0 && columns.some(isReadinessColumn),
    placeholderData: keepPreviousData,
  });
  const total = list.data?.total ?? 0;
  const servedPage = list.data?.page;

  // The server clamps a page past the end (a filter shrank the result) and
  // reports the page it used. Only fresh data reconciles, so this never fights
  // a page the admin just clicked.
  useEffect(() => {
    if (servedPage === undefined || list.isPlaceholderData) return;
    if (servedPage === params.page) return;

    navigate({
      search: (previous) => ({
        ...previous,
        page: servedPage === 1 ? undefined : servedPage,
      }),
      replace: true,
    });
  }, [servedPage, list.isPlaceholderData, params.page, navigate]);

  const [selected, setSelected] = useState<
    ReadonlyMap<string, CoachDirectoryRow>
  >(() => new Map());
  const selectedIds = new Set(selected.keys());

  const toggleRow = (coach: CoachDirectoryRow) =>
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(coach.id)) next.delete(coach.id);
      else next.set(coach.id, coach);
      return next;
    });

  const togglePage = (select: boolean) =>
    setSelected((current) => {
      const next = new Map(current);
      for (const coach of rows) {
        if (select) next.set(coach.id, coach);
        else next.delete(coach.id);
      }
      return next;
    });

  // Selected rows are snapshots from when they were ticked. Swapping in the
  // current page's copy keeps an export or email copy in step with actions
  // taken since.
  const currentSelection = (): CoachDirectoryRow[] =>
    Array.from(
      selected.values(),
      (coach) => rows.find((row) => row.id === coach.id) ?? coach,
    );

  const [sheetCoach, setSheetCoach] = useState<CoachDirectoryRow | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // The open coach is its own query, keyed by id, so an action that moves them
  // out of the current filter still shows its result. The clicked row seeds
  // it, and it refetches only when an action invalidates the directory.
  const sheetCoachQuery = useQuery({
    ...adminCoachDirectoryListQueryOptions({
      search: sheetCoach?.id ?? "",
      pageSize: 1,
    }),
    enabled: sheetCoach !== null,
    initialData: () =>
      sheetCoach === null
        ? undefined
        : { rows: [sheetCoach], total: 1, page: 1, pageSize: 1 },
    staleTime: Number.POSITIVE_INFINITY,
  });
  const liveSheetCoach = sheetCoachQuery.data?.rows[0] ?? sheetCoach;

  const exportMutation = useMutation({
    mutationFn: async (exportColumns: readonly CoachDirectoryColumnId[]) => {
      const result = await exportAdminCoachDirectoryFn({
        data: {
          ...facets,
          search: params.search,
          sort: params.sort,
          direction: params.direction,
        },
      });
      downloadCsv(
        buildCoachDirectoryCsv(
          result.rows,
          exportColumns,
          window.location.origin,
        ),
        "coaches",
      );
      return result;
    },
    onSuccess: (result) => {
      if (result.rows.length < result.total) {
        toast.warning(
          `Exported the first ${result.rows.length.toLocaleString("en-US")} of ${result.total.toLocaleString("en-US")} coaches. Narrow the filters to export the rest.`,
        );
      } else {
        toast.success(
          `Exported ${result.total.toLocaleString("en-US")} coaches.`,
        );
      }
    },
    onError: () => toast.error("The export failed, so nothing downloaded."),
  });

  const copySelectedEmails = () => {
    const emails = currentSelection().map((coach) => coach.email);
    navigator.clipboard.writeText(emails.join(", ")).then(
      () =>
        toast.success(
          `Copied ${emails.length} ${emails.length === 1 ? "email" : "emails"}.`,
        ),
      () => toast.error("Could not copy to the clipboard."),
    );
  };

  const changeSort = (sort: CoachDirectorySort) =>
    updateSearch({
      ...nextSortSearch(
        { sort: params.sort, direction: params.direction },
        sort,
        SORT_DEFAULTS,
      ),
      page: undefined,
    });

  const hasQuery =
    params.search !== "" ||
    FACET_KEYS.some((facet) => facets[facet] !== undefined);

  return (
    <div className="space-y-5 pb-24">
      <header>
        <h1 className="text-2xl font-medium text-gray-950">Coaches</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every coach account with its approval, billing, Nova use, client
          activity, inbox, and use of the wider platform.
        </p>
      </header>

      <CoachDirectorySummaryStrip
        summary={summary.data}
        facets={facets}
        onSelect={(next) => updateSearch(facetSearchPatch(next))}
      />

      <div className="space-y-3">
        <CoachDirectoryViewTabs
          view={view}
          isCustom={isCustomColumns}
          permissions={adminPermissions}
          onViewChange={(next) =>
            updateSearch({
              view: next === DEFAULT_VIEW ? undefined : next,
              cols: undefined,
            })
          }
        />
        <CoachDirectoryToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          isFetching={list.isFetching}
          facets={facets}
          onFacetsChange={(next) => updateSearch(facetSearchPatch(next))}
          view={view}
          columns={columns}
          isCustom={isCustomColumns}
          onColumnsChange={(cols) => updateSearch({ cols }, { replace: true })}
          permissions={adminPermissions}
          total={total}
          isExporting={exportMutation.isPending}
          onExport={(exportColumns) => exportMutation.mutate(exportColumns)}
        />
        <ActiveFilterChips
          facets={facets}
          onFacetsChange={(next) => updateSearch(facetSearchPatch(next))}
        />
      </div>

      {readiness.isError ? (
        <p role="alert" className="text-sm text-red-700">
          Could not load the readiness results. Reload the page to try again.
        </p>
      ) : null}

      {rows.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white">
          {hasQuery ? (
            <EmptyState
              icon={SearchX}
              title="No coaches match"
              description="Try another search, or remove a filter."
            >
              <Button
                onClick={() => {
                  setSearchInput("");
                  updateSearch({ ...facetSearchPatch({}), q: undefined });
                }}
              >
                Clear search and filters
              </Button>
            </EmptyState>
          ) : (
            <EmptyState
              icon={Users}
              title="No coaches yet"
              description="Coaches appear here as soon as they register."
            />
          )}
        </div>
      ) : (
        <>
          <CoachDirectoryTable
            rows={rows}
            columns={columns}
            sort={params.sort}
            direction={params.direction}
            onSort={changeSort}
            selectedIds={selectedIds}
            onToggleRow={toggleRow}
            onTogglePage={togglePage}
            onOpenCoach={(coach) => {
              setSheetCoach(coach);
              setIsSheetOpen(true);
            }}
            isStale={list.isPlaceholderData}
            now={now}
            readiness={readiness.data}
          />
          <CoachDirectoryPagination
            page={servedPage ?? params.page}
            pageSize={params.pageSize}
            total={total}
            onPageChange={(page) =>
              updateSearch({ page: page === 1 ? undefined : page })
            }
            onPageSizeChange={(size) =>
              updateSearch({
                size: size === DEFAULT_PAGE_SIZE ? undefined : size,
                page: undefined,
              })
            }
          />
        </>
      )}

      <SelectionBar
        count={selected.size}
        onCopyEmails={copySelectedEmails}
        onExport={() =>
          downloadCsv(
            buildCoachDirectoryCsv(
              currentSelection(),
              columns,
              window.location.origin,
            ),
            "coaches-selected",
          )
        }
        onClear={() => setSelected(new Map())}
      />

      <CoachDetailSheet
        coach={liveSheetCoach}
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        now={now}
        canSeeReadiness={canSeeReadiness}
        canEditCoaches={canEditCoaches}
      />
    </div>
  );
}
